import React, { useEffect, useRef, useState } from "react";
import {
  FaArrowUpRightFromSquare,
  FaCaretDown,
  FaCircle,
} from "react-icons/fa6";
import { RingProgress } from "@mantine/core";

const PageCode = ({ codeSecurityRef }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleModal = () => {
    setModalOpen(!modalOpen);
  };

  useEffect(() => {
    // Function to handle scroll locking
    const handleScrollLock = () => {
      if (modalOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    };

    handleScrollLock(); // Initial check when component mounts

    return () => {
      document.body.style.overflow = "auto"; // Reset overflow style when component unmounts
    };
  }, [modalOpen]);

  const critical = 1;
  const major = 0;
  const medium = 0;
  const minor = 0;
  const informational = 1;
  const total = critical + major + medium + minor + informational;

  return (
    <div ref={codeSecurityRef} id="code-security">
      <div className="flex flex-col gap-3 sm:gap-4">
        <div class="hidden sm:block">
          <div class="text-2xl font-semibold text-neutral-100 dark:text-neutral-0 flex items-center gap-4">
            <div>Security</div>
            <div class="w-[20px]">
              <div class="flex flex-col gap-[0.125rem]">
                <div class="flex w-full items-center gap-[0.125rem]">
                  <div class="grow bg-current text-score-alert z-10 mx-[-3px] h-[20px] rounded-lg border-3 border-opacity-30 bg-clip-content"></div>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between gap-2 lg:gap-4 ">
              <div>
                <span class="text-lg font-medium lg:text-4xl md-lg:text-3xl">
                  24.00
                </span>
                <span class="text-xs font-normal ml-1 uppercase">
                  Category Score
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          className="flex flex-col gap-4 sm:p-4 sm:bg-neutral-5 sm:dark:bg-neutral-95 border-neutral-20 dark:border-neutral-80 sm:border"
          style={{ minHeight: "420px" }}
        >
          <div className="flex flex-col gap-4">
            <div className="bg-neutral-0 p-4 shadow-card dark:bg-neutral-100 sm:shadow-none empty:hidden rounded-lg">
              <div className="flex flex-col gap-4">
                <div class="flex flex-col gap-2">
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-xl font-semibold text-neutral-100 dark:text-neutral-0">
                      Code Audit History
                    </span>

                    <div class="flex shrink-0 grow-0 items-center gap-2">
                      <button
                        type="button"
                        class="box-border flex items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-bg-colors focus-visible:outline-none focus-visible:ring gap-2 text-sm h-8 px-2.5 border border-neutral-100 text-neutral-100 hover:bg-neutral-10 active:bg-neutral-20 focus-visible:ring-secondary-focus dark:border-neutral-0 dark:text-neutral-0 dark:hover:bg-neutral-90 dark:active:bg-neutral-80 dark:focus-visible:ring-secondary-focus-dark"
                      >
                        View PDF
                      </button>
                      <div class="hidden md:block">
                        <button
                          onClick={handleModal}
                          class="box-border flex items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-bg-colors focus-visible:outline-none focus-visible:ring gap-2 text-sm h-8 px-2.5 bg-neutral-100 dark:brightness-90 text-neutral-0 hover:bg-neutral-70 focus-visible:ring-secondary-focus active:bg-neutral-50 dark:text-neutral-100 dark:bg-neutral-0 dark:hover:bg-neutral-30 dark:active:bg-neutral-50 dark:focus-visible:ring-secondary-focus-dark"
                        >
                          View Findings
                        </button>
                      </div>

                      <div class="block w-full md:hidden">
                        <button
                          onClick={handleModal}
                          class="box-border flex items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-bg-colors focus-visible:outline-none focus-visible:ring gap-2 text-sm h-8 px-2.5 bg-neutral-100 dark:brightness-90 text-neutral-0 hover:bg-neutral-70 focus-visible:ring-secondary-focus active:bg-neutral-50 dark:text-neutral-100 dark:bg-neutral-0 dark:hover:bg-neutral-30 dark:active:bg-neutral-50 dark:focus-visible:ring-secondary-focus-dark"
                        >
                          View Findings
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex w-full justify-between gap-4">
                      <div className="flex min-w-0 shrink grow flex-wrap-reverse items-center justify-between gap-4">
                        <div className="relative w-full">
                          <div className="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide -my-4 py-1">
                            <div class="hidden whitespace-nowrap text-xs font-medium text-neutral-70 dark:text-neutral-30 lg:block">
                              1 Audit available | Last Audit was delivered on
                              7/14/2023
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-hidden">
                    <div>
                      <div className="grid grid-cols-12 gap-x-4 gap-y-4">
                        <div className="col-span-12 flex w-full flex-col gap-3 rounded-xl bg-background-light p-6 dark:bg-background-dark xl:col-span-6 h-[342px] border border-neutral-5 dark:border-neutral-95">
                          <div className="flex items-center justify-between gap-10">
                            <div class="relative">
                              <RingProgress
                                thickness={16}
                                size={110}
                                sections={[
                                  {
                                    value: `${(critical / total) * 100}`,
                                    color: "#da5c57",
                                  },
                                  {
                                    value: `${(major / total) * 100}`,
                                    color: "#d98e60",
                                  },
                                  {
                                    value: `${(medium / total) * 100}`,
                                    color: "#dbac5a",
                                  },
                                  {
                                    value: `${(minor / total) * 100}`,
                                    color: "#cdb225",
                                  },
                                  {
                                    value: `${(informational / total) * 100}`,
                                    color: "#50637e",
                                  },
                                ]}
                                tooltipDataSource="segment"
                              />
                              <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></div>
                            </div>
                            <div className="grid w-full grid-cols-4 items-center gap-4">
                              <div className="flex flex-col gap-1">
                                <div class="flex justify-center text-3xl font-semibold text-neutral-100 dark:text-neutral-0">
                                  {total}
                                </div>
                                <div class="truncate text-center text-xs font-normal text-neutral-60 dark:text-neutral-40">
                                  All Findings
                                </div>
                              </div>
                              <div class="flex flex-col gap-1">
                                <div class="flex justify-center text-3xl font-semibold text-neutral-70">
                                  {total}
                                </div>
                                <div class="flex justify-center gap-1 text-center text-xs font-normal text-neutral-60 dark:text-neutral-40">
                                  <div class="truncate">Acknowledged</div>
                                  <div class="flex gap-1"></div>
                                </div>
                              </div>
                              <div class="flex flex-col gap-1">
                                <div class="flex justify-center text-3xl font-semibold text-[#B1C6A1]">
                                  0
                                </div>
                                <div class="flex justify-center gap-1 text-center text-xs font-normal text-neutral-60 dark:text-neutral-40">
                                  <div class="truncate">Partially</div>
                                  <div class="flex gap-1"></div>
                                </div>
                              </div>
                              <div class="flex flex-col gap-1">
                                <div class="flex justify-center text-3xl font-semibold text-[#60A085]">
                                  0
                                </div>
                                <div class="flex justify-center gap-1 text-center text-xs font-normal text-neutral-60 dark:text-neutral-40">
                                  <div class="truncate">Resolved</div>
                                  <div class="flex gap-1"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="h-px w-full shrink-0 bg-neutral-20 dark:bg-neutral-80"></div>
                          <div className="flex h-full w-full flex-col justify-between">
                            <div className="-mx-3 w-full cursor-pointer rounded-lg px-3 py-1 transition-colors hover:bg-neutral-0 dark:hover:bg-neutral-100 opacity-100">
                              <div className="hidden w-full flex-row items-center gap-3 md:flex">
                                <div class="flex w-40 items-center gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <FaCircle class="mr-1 h-3 w-3 rounded-sm text-findings-critical" />
                                  <div class="w-5 text-base font-semibold text-findings-critical">
                                    {critical}
                                  </div>
                                  <div class="font-medium">Critical</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {critical > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="flex w-full flex-row items-center gap-1 md:hidden">
                                <div class="flex w-32 gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <div class="w-5 font-semibold text-findings-critical">
                                    {critical}
                                  </div>
                                  <div class="font-medium">Critical</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {critical > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="flex min-w-0 flex-1 items-center"></div>
                              </div>
                            </div>
                            <div class="-mx-3 w-full cursor-pointer rounded-lg px-3 py-1 transition-colors hover:bg-neutral-0 dark:hover:bg-neutral-100 opacity-100">
                              <div class="hidden w-full flex-row items-center gap-3 md:flex">
                                <div class="flex w-40 items-center gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <FaCircle class="mr-1 h-3 w-3 rounded-sm text-findings-major" />
                                  <div class="w-5 text-base font-semibold text-findings-major">
                                    {major}
                                  </div>
                                  <div class="font-medium">Major</div>
                                </div>

                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {major > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="flex w-full flex-row items-center gap-1 md:hidden">
                                <div class="flex w-32 gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <div class="w-5 font-semibold text-findings-major">
                                    {major}
                                  </div>
                                  <div class="font-medium">Major</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {major > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="-mx-3 w-full cursor-pointer rounded-lg px-3 py-1 transition-colors hover:bg-neutral-0 dark:hover:bg-neutral-100 opacity-100">
                              <div class="hidden w-full flex-row items-center gap-3 md:flex">
                                <div class="flex w-40 items-center gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <FaCircle class="mr-1 h-3 w-3 rounded-sm text-findings-medium" />
                                  <div class="w-5 text-base font-semibold text-findings-medium">
                                    {medium}
                                  </div>
                                  <div class="font-medium">Medium</div>
                                </div>

                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {medium > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="flex w-full flex-row items-center gap-1 md:hidden">
                                <div class="flex w-32 gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <div class="w-5 font-semibold text-findings-medium">
                                    {medium}
                                  </div>
                                  <div class="font-medium">Medium</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {medium > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="flex min-w-0 flex-1 items-center"></div>
                              </div>
                            </div>
                            <div class="-mx-3 w-full cursor-pointer rounded-lg px-3 py-1 transition-colors hover:bg-neutral-0 dark:hover:bg-neutral-100 opacity-100">
                              <div class="hidden w-full flex-row items-center gap-3 md:flex">
                                <div class="flex w-40 items-center gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <FaCircle class="mr-1 h-3 w-3 rounded-sm text-score-fair" />
                                  <div class="w-5 text-base font-semibold text-score-fair">
                                    {minor}
                                  </div>
                                  <div class="font-medium">Minor</div>
                                </div>

                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {minor > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="flex w-full flex-row items-center gap-1 md:hidden">
                                <div class="flex w-32 gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <div class="w-5 font-semibold text-score-fair">
                                    {minor}
                                  </div>
                                  <div class="font-medium">Minor</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {minor > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="-mx-3 w-full cursor-pointer rounded-lg px-3 py-1 transition-colors hover:bg-neutral-0 dark:hover:bg-neutral-100 opacity-100">
                              <div class="hidden w-full flex-row items-center gap-3 md:flex">
                                <div class="flex w-40 items-center gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <FaCircle class="mr-1 h-3 w-3 rounded-sm text-findings-informational" />
                                  <div class="w-5 text-base font-semibold text-findings-informational">
                                    {informational}
                                  </div>
                                  <div class="font-medium">Informational</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {informational > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="flex w-full flex-row items-center gap-1 md:hidden">
                                <div class="flex w-32 gap-2 text-sm text-neutral-100 dark:text-neutral-0">
                                  <div class="w-5 font-semibold text-findings-informational">
                                    {informational}
                                  </div>
                                  <div class="font-medium">Informational</div>
                                </div>
                                <div class="w-80 greyscale-[50%] opacity-30">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="relative flex items-center gap-1">
                                        {informational > 0 ? (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-score-excellent text-neutral-10 dark:bg-score-excellent dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            Acknowledged
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            class="flex w-fit items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-semibold capitalize transition-colors bg-neutral-90 text-neutral-10 dark:bg-neutral-80 dark:text-neutral-0 dark:hover:bg-neutral-70 text-xs"
                                          >
                                            None
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="relative w-full grow"></div>
                        </div>
                        <div className="col-span-12 flex flex-col gap-y-4 xl:gap-y-5 rounded-xl bg-background-light p-6 dark:bg-background-dark xl:col-span-3 md:col-span-6 xl:grid-cols-1 border border-neutral-5 dark:border-neutral-95">
                          <div class="flex h-fit flex-col gap-2">
                            <div
                              class="text-sm font-normal text-neutral-100 dark:text-neutral-0"
                              style={{
                                borderBottom: "2px solid white",
                                paddingBottom: "16px",
                              }}
                            >
                              Token Analysis
                            </div>
                          </div>
                          <div className="flex gap-2 items-center justify-between">
                            <div class="flex flex-col gap-2 h-full">
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Token Decimals:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Total Supply:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Platform:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Compiler:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Sol License:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Contract Name:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Contract Created:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Contract Language:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Owner Address:
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Deployer Address:
                              </div>
                            </div>
                            <div class="flex flex-col gap-2 h-full items-end">
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                9
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                100.000.000
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Binance Smart Chain
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                v0.8.17+commit.8df45f5f
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                No License (None)
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                MEMEPOPEYE
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                23/01/2024
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                Solidity
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                <div class="flex w-full items-center gap-1 dark:text-neutral-0">
                                  <img
                                    alt="Verification Logo"
                                    loading="lazy"
                                    width="12"
                                    height="12"
                                    decoding="async"
                                    data-nimg="1"
                                    class="flex dark:hidden"
                                    src="https://skynet.certik.com/_next/static/media/bscscan.606a92a8.svg"
                                    style={{ color: "transparent" }}
                                  />
                                  <img
                                    alt="Verification Dark Mode Logo"
                                    loading="lazy"
                                    width="12"
                                    height="12"
                                    decoding="async"
                                    data-nimg="1"
                                    class="hidden dark:flex"
                                    src="https://skynet.certik.com/_next/static/media/bscscan-dark.e9b42ffa.svg"
                                    style={{ color: "transparent" }}
                                  />
                                  <span>0x0000..0000</span>
                                  <a
                                    href="https://bscscan.com/address/0x0000000000000000000000000000000000000000"
                                    rel="noreferrer"
                                    target="_blank"
                                  >
                                    <FaArrowUpRightFromSquare className="text-neutral-50" />
                                  </a>
                                </div>
                              </div>
                              <div class="text-xs font-normal text-neutral-100 dark:text-neutral-0">
                                <div class="flex w-full items-center gap-1 dark:text-neutral-0">
                                  <img
                                    alt="Verification Logo"
                                    loading="lazy"
                                    width="12"
                                    height="12"
                                    decoding="async"
                                    data-nimg="1"
                                    class="flex dark:hidden"
                                    src="https://skynet.certik.com/_next/static/media/bscscan.606a92a8.svg"
                                    style={{ color: "transparent" }}
                                  />
                                  <img
                                    alt="Verification Dark Mode Logo"
                                    loading="lazy"
                                    width="12"
                                    height="12"
                                    decoding="async"
                                    data-nimg="1"
                                    class="hidden dark:flex"
                                    src="https://skynet.certik.com/_next/static/media/bscscan-dark.e9b42ffa.svg"
                                    style={{ color: "transparent" }}
                                  />
                                  <span>0x0000..0000</span>
                                  <a
                                    href="https://bscscan.com/address/0x0000000000000000000000000000000000000000"
                                    rel="noreferrer"
                                    target="_blank"
                                  >
                                    <FaArrowUpRightFromSquare className="text-neutral-50" />
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-span-12 flex flex-col gap-y-4 xl:gap-y-5 rounded-xl bg-background-light p-6 dark:bg-background-dark xl:col-span-3 md:col-span-6 xl:grid-cols-1 border border-neutral-5 dark:border-neutral-95">
                          <div class="flex h-fit flex-col gap-2">
                            <div class="text-sm font-normal text-neutral-100 dark:text-neutral-0">
                              Audit Overview
                            </div>
                            <div class="flex flex-wrap gap-2">
                              <div class="flex items-center gap-1 truncate rounded px-1 py-0.5 font-semibold capitalize bg-neutral-10 text-neutral-100 dark:bg-neutral-90 dark:text-neutral-0 text-xs">
                                Manual Review
                              </div>
                              <div class="flex items-center gap-1 truncate rounded px-1 py-0.5 font-semibold capitalize bg-neutral-10 text-neutral-100 dark:bg-neutral-90 dark:text-neutral-0 text-xs">
                                Static Analysis
                              </div>
                              <div class="flex items-center gap-1 truncate rounded px-1 py-0.5 font-semibold capitalize bg-neutral-10 text-neutral-100 dark:bg-neutral-90 dark:text-neutral-0 text-xs">
                                Testnet Deployment
                              </div>
                            </div>
                          </div>
                          <div class="flex flex-col gap-2 h-full">
                            <div class="text-sm font-normal text-neutral-100 dark:text-neutral-0">
                              Audit Timeline
                            </div>
                            <div class="w-60 text-left text-sm font-light flex h-full">
                              <div class="flex flex-col gap-3">
                                <div class="flex flex-col gap-1 h-full">
                                  <div class="flex flex-col overflow-hidden gap-2 flex-grow justify-between h-full">
                                    <div style={{ zIndex: 2 }}>
                                      <div class="flex flex-row items-start gap-2 text-neutral-100 dark:text-neutral-0">
                                        <div class="relative flex flex-shrink-0 flex-col">
                                          <div class="rounded-full bg-neutral-0 dark:bg-neutral-100">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              fill="currentColor"
                                              viewBox="0 0 256 256"
                                              class="text-neutral-30 dark:text-neutral-70"
                                            >
                                              <rect
                                                width="256"
                                                height="256"
                                                fill="none"
                                              ></rect>
                                              <path d="M128,24A104,104,0,1,0,232,128,104.2,104.2,0,0,0,128,24Zm49.5,85.8-58.6,56a8.1,8.1,0,0,1-5.6,2.2,7.7,7.7,0,0,1-5.5-2.2l-29.3-28a8,8,0,1,1,11-11.6l23.8,22.7,53.2-50.7a8,8,0,0,1,11,11.6Z"></path>
                                            </svg>
                                          </div>
                                        </div>
                                        <div class="flex flex-grow flex-col">
                                          <div
                                            class="flex flex-row gap-1 text-sm font-normal cursor-default"
                                            role="button"
                                            tabindex="0"
                                          >
                                            Requested on 4/21/2023
                                          </div>
                                          <div
                                            class="flex h-0 flex-row overflow-hidden transition-all text-sm"
                                            style={{ height: "0px" }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ zIndex: 2 }}>
                                      <div class="flex flex-row items-start gap-2 text-neutral-100 dark:text-neutral-0">
                                        <div class="relative flex flex-shrink-0 flex-col">
                                          <div class="rounded-full bg-neutral-0 dark:bg-neutral-100">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              fill="currentColor"
                                              viewBox="0 0 256 256"
                                              class="text-neutral-30 dark:text-neutral-70"
                                            >
                                              <rect
                                                width="256"
                                                height="256"
                                                fill="none"
                                              ></rect>
                                              <path d="M128,24A104,104,0,1,0,232,128,104.2,104.2,0,0,0,128,24Zm49.5,85.8-58.6,56a8.1,8.1,0,0,1-5.6,2.2,7.7,7.7,0,0,1-5.5-2.2l-29.3-28a8,8,0,1,1,11-11.6l23.8,22.7,53.2-50.7a8,8,0,0,1,11,11.6Z"></path>
                                            </svg>
                                          </div>
                                        </div>
                                        <div class="flex flex-grow flex-col">
                                          <div
                                            class="flex flex-row gap-1 text-sm font-normal cursor-default"
                                            role="button"
                                            tabindex="0"
                                          >
                                            Onboarding on 4/21/2023
                                          </div>
                                          <div
                                            class="flex h-0 flex-row overflow-hidden transition-all text-sm"
                                            style={{ height: "0px" }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ zIndex: 2 }}>
                                      <div class="flex flex-row items-start gap-2 text-neutral-100 dark:text-neutral-0">
                                        <div class="relative flex flex-shrink-0 flex-col">
                                          <div class="rounded-full bg-neutral-0 dark:bg-neutral-100">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              fill="currentColor"
                                              viewBox="0 0 256 256"
                                              class="text-neutral-30 dark:text-neutral-70"
                                            >
                                              <rect
                                                width="256"
                                                height="256"
                                                fill="none"
                                              ></rect>
                                              <path d="M128,24A104,104,0,1,0,232,128,104.2,104.2,0,0,0,128,24Zm49.5,85.8-58.6,56a8.1,8.1,0,0,1-5.6,2.2,7.7,7.7,0,0,1-5.5-2.2l-29.3-28a8,8,0,1,1,11-11.6l23.8,22.7,53.2-50.7a8,8,0,0,1,11,11.6Z"></path>
                                            </svg>
                                          </div>
                                        </div>
                                        <div class="flex flex-grow flex-col">
                                          <div
                                            class="flex flex-row gap-1 text-sm font-normal cursor-default"
                                            role="button"
                                            tabindex="0"
                                          >
                                            Review on 4/21/2023
                                          </div>
                                          <div
                                            class="flex h-0 flex-row overflow-hidden transition-all text-sm"
                                            style={{ height: "0px" }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ zIndex: 1 }}>
                                      <div class="flex flex-row items-start gap-2 text-neutral-100 dark:text-neutral-0">
                                        <div class="relative flex flex-shrink-0 flex-col">
                                          <div class="rounded-full bg-neutral-0 dark:bg-neutral-100">
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              width="20"
                                              height="20"
                                              fill="currentColor"
                                              viewBox="0 0 256 256"
                                              class="text-neutral-100 dark:text-neutral-0"
                                            >
                                              <rect
                                                width="256"
                                                height="256"
                                                fill="none"
                                              ></rect>
                                              <path d="M128,24A104,104,0,1,0,232,128,104.2,104.2,0,0,0,128,24Zm49.5,85.8-58.6,56a8.1,8.1,0,0,1-5.6,2.2,7.7,7.7,0,0,1-5.5-2.2l-29.3-28a8,8,0,1,1,11-11.6l23.8,22.7,53.2-50.7a8,8,0,0,1,11,11.6Z"></path>
                                            </svg>
                                          </div>
                                          <div class="absolute -top-[100vh] left-1/2 h-screen w-0 -translate-x-1/2 border-l-2 bg-neutral-0 dark:bg-neutral-100 border-neutral-100 dark:border-neutral-0"></div>
                                        </div>
                                        <div class="flex flex-grow flex-col">
                                          <div
                                            class="flex flex-row gap-1 text-sm font-normal cursor-default"
                                            role="button"
                                            tabindex="0"
                                          >
                                            Release on 12/11/2023
                                          </div>
                                          <div
                                            class="flex h-0 flex-row overflow-hidden transition-all text-sm"
                                            style={{ height: "0px" }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {modalOpen ? (
        <div class="relative">
          <div class="fixed inset-0 bg-neutral-100 bg-opacity-60 transition-opacity z-modalBackdrop opacity-100"></div>
          <div class="fixed inset-0 overflow-y-auto z-modal">
            <div class="flex min-h-full items-center justify-center">
              <div
                class="relative transform rounded-xl shadow-xl shadow-black-5 dark:shadow-black-40 max-h-[80vh] sm:max-h-[none] bg-neutral-0 dark:bg-neutral-100 border border-neutral-20 dark:border-neutral-80 overflow-hidden overflow-y-scroll scrollbar-hide sm:overflow-y-auto text-neutral-100 dark:text-neutral-0 h-full w-full sm:max-w-[calc(100%-32px)] lg:max-w-[1000px] opacity-100 translate-y-0 scale-100"
                id="headlessui-dialog-panel-:r3l:"
                data-headlessui-state="open"
              >
                <h3
                  id="headlessui-dialog-title-:r3m:"
                  data-headlessui-state="open"
                >
                  <span class="text-base font-semibold leading-6 text-neutral-100 dark:text-neutral-0 sm:text-xl"></span>
                </h3>
                <div class="mt-0">
                  <div class="h-[100vh] sm:h-[80vh]">
                    <div class="flex h-full flex-col md:flex-row">
                      <div class="hidden min-w-[33%] basis-4/12 md:block">
                        <div class="flex h-full flex-col border-r border-neutral-10 bg-background-light dark:border-neutral-90 dark:bg-background-dark">
                          <div class="flex flex-col gap-2 md:mb-4 md:ml-6 md:mr-4 md:mt-6">
                            <div>
                              <div class="flex items-center justify-between gap-2">
                                <div class="relative w-full">
                                  <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                    <div class="whitespace-nowrap text-sm font-medium text-neutral-0 dark:text-neutral-0 md:whitespace-normal">
                                      Baby Troll - Audit
                                      {modalOpen ? "da" : "nu"}
                                      <span class="md:hidden">
                                        Findings Detail
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="hidden text-xl font-semibold text-neutral-100 dark:text-neutral-0 md:block">
                                Code Audit Findings
                              </div>
                            </div>
                            <div class="flex gap-2">
                              <div class="flex items-center gap-1 truncate rounded px-1 py-0.5 font-semibold capitalize bg-neutral-10 text-neutral-100 dark:bg-neutral-90 dark:text-neutral-0 text-xs">
                                8 Findings
                              </div>
                            </div>
                          </div>
                          <div class="h-px shrink-0 bg-neutral-10 dark:bg-neutral-90"></div>
                        </div>
                      </div>
                      <div class="block md:hidden">
                        <div>
                          <div class="hidden md:block">
                            <div class="flex flex-col gap-2 md:mb-4 md:ml-6 md:mr-4 md:mt-6">
                              <div>
                                <div class="flex items-center justify-between gap-2">
                                  <div class="relative w-full">
                                    <div class="max-w-full overflow-y-hidden overflow-x-scroll scroll-smooth scrollbar-hide">
                                      <div class="whitespace-nowrap text-sm font-medium text-neutral-0 dark:text-neutral-0 md:whitespace-normal">
                                        Baby Troll - Audit{" "}
                                        <span class="md:hidden">
                                          Findings Detail
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="hidden text-xl font-semibold text-neutral-100 dark:text-neutral-0 md:block">
                                  Code Audit Findings
                                </div>
                              </div>
                              <div class="flex gap-2">
                                <div class="flex items-center gap-1 truncate rounded px-1 py-0.5 font-semibold capitalize bg-neutral-10 text-neutral-100 dark:bg-neutral-90 dark:text-neutral-0 text-xs">
                                  8 Findings
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="mx-6">
                            <div data-headlessui-state="">
                              <div class="relative z-dropdown w-full">
                                <button
                                  class="group w-full outline-none"
                                  id="headlessui-listbox-button-:r3q:"
                                  type="button"
                                  aria-haspopup="listbox"
                                  aria-expanded="false"
                                  data-headlessui-state=""
                                >
                                  <div
                                    class="flex items-center justify-between gap-1 p-[5px] pl-3 pr-2 font-medium transition-colors text-xs w-full text-neutral-100 dark:text-neutral-0 rounded-full border ui-not-open:border-neutral-20 ui-not-open:bg-neutral-0 ui-not-open:dark:border-neutral-80 ui-not-open:dark:bg-neutral-100 ui-not-open:hover:bg-neutral-5 ui-not-open:dark:hover:bg-neutral-95 ui-open:hover:bg-neutral-5 ui-open:dark:hover:bg-neutral-95 ui-open:border-neutral-40 ui-open:bg-neutral-10 dark:ui-open:border-neutral-60 dark:ui-open:bg-neutral-90 ui-disabled:border-neutral-20 ui-disabled:bg-neutral-10 ui-disabled:text-neutral-40 dark:ui-disabled:border-neutral-80 dark:ui-disabled:bg-neutral-90 dark:ui-disabled:text-neutral-60"
                                    role="button"
                                    tabindex="0"
                                  >
                                    <div class="truncate">
                                      Initial Token Distribution
                                    </div>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                      class="text-neutral-70 transition-colors dark:text-neutral-30 "
                                    >
                                      <rect
                                        width="24"
                                        height="24"
                                        fill="none"
                                      ></rect>
                                      <path d="M17.9608 9.81051C17.9191 9.71121 17.8489 9.62648 17.7591 9.56701C17.6693 9.50754 17.5639 9.47598 17.4561 9.47632H6.54386C6.43614 9.47598 6.33073 9.50754 6.24092 9.56701C6.1511 9.62648 6.0809 9.71121 6.03916 9.81051C5.99985 9.91129 5.98988 10.0211 6.0104 10.1274C6.03092 10.2336 6.08109 10.3318 6.15511 10.4107L11.6113 15.8668C11.7155 15.9677 11.8549 16.024 12 16.024C12.1451 16.024 12.2845 15.9677 12.3888 15.8668L17.8449 10.4107C17.9189 10.3318 17.9691 10.2336 17.9896 10.1274C18.0101 10.0211 18.0001 9.91129 17.9608 9.81051Z"></path>
                                    </svg>
                                  </div>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="flex min-h-0 flex-col md:max-w-[66%] md:basis-8/12 md:flex-row">
                        <div class="mx-6 mt-6 flex min-h-0 w-10/12 flex-col md:mx-12 md:mt-8 md:w-11/12 lg:w-11/12"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="absolute right-0 top-0 pr-3 pt-3 opacity-50 transition-opacity hover:opacity-100">
                  <button
                    onClick={handleModal}
                    type="button"
                    class="box-border flex items-center justify-center whitespace-nowrap rounded-lg font-semibold transition-bg-colors focus-visible:outline-none focus-visible:ring gap-2 text-sm h-8 w-8 text-neutral-100 hover:bg-neutral-10 active:bg-neutral-20 focus-visible:ring-secondary-focus dark:text-neutral-0 dark:hover:bg-neutral-90 dark:active:bg-neutral-80 dark:focus-visible:ring-secondary-focus-dark"
                    aria-label="Close"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 256 256"
                      class="shrink-0"
                    >
                      <rect width="256" height="256" fill="none"></rect>
                      <line
                        x1="200"
                        y1="56"
                        x2="56"
                        y2="200"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="16"
                      ></line>
                      <line
                        x1="200"
                        y1="200"
                        x2="56"
                        y2="56"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="16"
                      ></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div data-headlessui-portal=""></div>
          <div data-headlessui-portal=""></div>
        </div>
      ) : null}
    </div>
  );
};

export default PageCode;
